import React, { useState, useEffect, useCallback } from 'react';
import { Container, Row, Col, Card, Table, Button, Badge, Nav, Pagination, Spinner, Alert } from 'react-bootstrap';
import TicketChat from './TicketChat';

const Trengo = () => {
    const [activeTab, setActiveTab] = useState('tickets');
    const [tickets, setTickets] = useState([]);
    const [brands, setBrands] = useState([]);
    const [selectedBrand, setSelectedBrand] = useState(null);
    const [brandTickets, setBrandTickets] = useState([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const [pagination, setPagination] = useState({
        currentPage: 1,
        totalPages: 1,
        totalItems: 0,
        limit: 20
    });
    const [selectedTicket, setSelectedTicket] = useState(null);

    const fetchData = useCallback(async (endpoint, params = {}) => {
        try {
            const token = localStorage.getItem('token');
            const queryParams = new URLSearchParams({
                limit: params.limit || 20,
                offset: params.offset || 0,
                ...params
            });
            
            const response = await fetch(`/api/trengo/${endpoint}?${queryParams}`, {
                headers: {
                    'Authorization': `Bearer ${token}`,
                    'Content-Type': 'application/json'
                }
            });
            
            if (!response.ok) {
                throw new Error(`HTTP error! status: ${response.status}`);
            }
            
            return await response.json();
        } catch (error) {
            console.error(`Error fetching ${endpoint}:`, error);
            throw error;
        }
    }, []);

    const loadTickets = useCallback(async (page = 1) => {
        setLoading(true);
        setError(null);
        try {
            const offset = (page - 1) * pagination.limit;
            const data = await fetchData('tickets', { offset, limit: pagination.limit });
            setTickets(data.data);
            setPagination(prev => ({
                ...prev,
                currentPage: page,
                totalPages: Math.ceil(data.total / pagination.limit),
                totalItems: data.total
            }));
        } catch (error) {
            setError('Failed to fetch tickets');
        } finally {
            setLoading(false);
        }
    }, [fetchData, pagination.limit]);

    const loadBrands = useCallback(async () => {
        try {
            const data = await fetchData('brands');
            setBrands(data.data);
        } catch (error) {
            console.error('Error loading brands:', error);
        }
    }, [fetchData]);

    const loadBrandTickets = useCallback(async (brandName, page = 1) => {
        setLoading(true);
        setError(null);
        try {
            const offset = (page - 1) * pagination.limit;
            const data = await fetchData(`tickets/by-brand/${brandName}`, { offset, limit: pagination.limit });
            setBrandTickets(data.data);
            setPagination(prev => ({
                ...prev,
                currentPage: page,
                totalPages: Math.ceil(data.total / pagination.limit),
                totalItems: data.total
            }));
        } catch (error) {
            setError(`Failed to fetch tickets for ${brandName}`);
        } finally {
            setLoading(false);
        }
    }, [fetchData, pagination.limit]);

    useEffect(() => {
        loadTickets();
        loadBrands();
    }, [loadTickets, loadBrands]);

    const handleBrandClick = (brand) => {
        setSelectedBrand(brand);
        setActiveTab('brand-tickets');
        loadBrandTickets(brand.name.toLowerCase());
    };

    const handleTicketClick = (ticket) => {
        setSelectedTicket(ticket);
    };

    const handleBackToBrands = () => {
        setSelectedBrand(null);
        setSelectedTicket(null);
        setActiveTab('tickets');
        loadTickets();
    };

    const handlePageChange = (page) => {
        if (selectedBrand) {
            loadBrandTickets(selectedBrand.name.toLowerCase(), page);
        } else {
            loadTickets(page);
        }
    };

    const renderTicketsTable = (ticketsData) => (
        <Table striped bordered hover responsive>
            <thead>
                <tr>
                    <th>ID</th>
                    <th>Subject</th>
                    <th>Status</th>
                    <th>Priority</th>
                    <th>Channel</th>
                    <th>Created</th>
                    <th>Actions</th>
                </tr>
            </thead>
            <tbody>
                {ticketsData.map(ticket => (
                    <tr key={ticket.id}>
                        <td>{ticket.trengo_id}</td>
                        <td>{ticket.subject || 'No subject'}</td>
                        <td>
                            <Badge bg={ticket.status === 'open' ? 'success' : 'secondary'}>
                                {ticket.status || 'Unknown'}
                            </Badge>
                        </td>
                        <td>{ticket.priority || 'N/A'}</td>
                        <td>
                            {ticket.channel?.display_name || ticket.channel?.name || 'Unknown Channel'}
                        </td>
                        <td>{new Date(ticket.created_at).toLocaleDateString()}</td>
                        <td>
                            <Button 
                                variant="outline-primary" 
                                size="sm"
                                onClick={() => handleTicketClick(ticket)}
                            >
                                Open Chat
                            </Button>
                        </td>
                    </tr>
                ))}
            </tbody>
        </Table>
    );

    const renderBrandsView = () => (
        <div>
            <h3>Brands & Channels</h3>
            <Row>
                {brands.map(brand => (
                    <Col key={brand.name} md={4} className="mb-3">
                        <Card 
                            className="h-100 cursor-pointer" 
                            onClick={() => handleBrandClick(brand)}
                            style={{ cursor: 'pointer' }}
                        >
                            <Card.Body>
                                <Card.Title>{brand.name}</Card.Title>
                                <Card.Text>
                                    <strong>Total Tickets:</strong> {brand.ticket_count}<br/>
                                    <strong>Channels:</strong> {brand.channels.length}
                                </Card.Text>
                                <div>
                                    {brand.channels.map(channel => (
                                        <Badge key={channel.id} bg="light" text="dark" className="me-1 mb-1">
                                            {channel.display_name}
                                        </Badge>
                                    ))}
                                </div>
                            </Card.Body>
                        </Card>
                    </Col>
                ))}
            </Row>
        </div>
    );

    const renderBrandTicketsView = () => (
        <div>
            <div className="d-flex justify-content-between align-items-center mb-3">
                <h3>{selectedBrand?.name} Tickets</h3>
                <Button variant="outline-secondary" onClick={handleBackToBrands}>
                    ← Back to Brands
                </Button>
            </div>
            
            <div className="mb-3">
                <h5>Channels:</h5>
                {selectedBrand?.channels.map(channel => (
                    <Badge key={channel.id} bg="info" className="me-2">
                        {channel.display_name} ({channel.ticket_count} tickets)
                    </Badge>
                ))}
            </div>

            {loading ? (
                <div className="text-center">
                    <Spinner animation="border" />
                </div>
            ) : error ? (
                <Alert variant="danger">{error}</Alert>
            ) : (
                <>
                    {renderTicketsTable(brandTickets)}
                    {pagination.totalPages > 1 && (
                                renderPagination(pagination.totalPages, pagination.currentPage, handlePageChange)
                    )}
                </>
            )}
        </div>
    );

    const renderPagination = (totalPages, currentPage, onPageChange) => {
        if (totalPages <= 1) return null;
        
        const maxVisiblePages = 10; // Show more pages
        let startPage = 1;
        let endPage = totalPages;
        
        if (totalPages > maxVisiblePages) {
            if (currentPage <= Math.ceil(maxVisiblePages / 2)) {
                endPage = maxVisiblePages;
            } else if (currentPage >= totalPages - Math.floor(maxVisiblePages / 2)) {
                startPage = totalPages - maxVisiblePages + 1;
            } else {
                startPage = currentPage - Math.floor(maxVisiblePages / 2);
                endPage = currentPage + Math.floor(maxVisiblePages / 2);
            }
        }
        
        const pages = [];
        
        // Add first page if not visible
        if (startPage > 1) {
            pages.push(
                <Pagination.Item key={1} onClick={() => onPageChange(1)}>
                    1
                </Pagination.Item>
            );
            if (startPage > 2) {
                pages.push(<Pagination.Ellipsis key="start-ellipsis" />);
            }
        }
        
        // Add visible pages
        for (let i = startPage; i <= endPage; i++) {
            pages.push(
                <Pagination.Item
                    key={i}
                    active={i === currentPage}
                    onClick={() => onPageChange(i)}
                >
                    {i}
                </Pagination.Item>
            );
        }
        
        // Add last page if not visible
        if (endPage < totalPages) {
            if (endPage < totalPages - 1) {
                pages.push(<Pagination.Ellipsis key="end-ellipsis" />);
            }
            pages.push(
                <Pagination.Item key={totalPages} onClick={() => onPageChange(totalPages)}>
                    {totalPages}
                </Pagination.Item>
            );
        }
        
        return (
            <Pagination className="justify-content-center">
                <Pagination.First 
                    onClick={() => onPageChange(1)}
                    disabled={currentPage === 1}
                />
                <Pagination.Prev 
                    onClick={() => onPageChange(currentPage - 1)}
                    disabled={currentPage === 1}
                />
                
                {pages}
                
                <Pagination.Next 
                    onClick={() => onPageChange(currentPage + 1)}
                    disabled={currentPage === totalPages}
                />
                <Pagination.Last 
                    onClick={() => onPageChange(totalPages)}
                    disabled={currentPage === totalPages}
                />
            </Pagination>
        );
    };

    if (selectedTicket) {
        return (
            <TicketChat 
                ticketId={selectedTicket.id} 
                onBack={() => setSelectedTicket(null)}
                ticketInfo={selectedTicket}
            />
        );
    }

    return (
        <Container fluid className="page-content">
            <div className="page-header">
                <h2>Trengo Management</h2>
                <div className="d-flex align-items-center">
                    <Badge bg="primary" className="me-3">
                        📊 Total Tickets: {pagination.totalItems}
                    </Badge>
                    <Badge bg="success" className="me-3">
                        📡 Channels: {brands.length > 0 ? brands.reduce((total, brand) => total + brand.channels.length, 0) : 0}
                    </Badge>
                    <Badge bg="info">
                        💬 Messages: {pagination.totalItems > 0 ? Math.round(pagination.totalItems * 6.8) : 0}
                    </Badge>
                </div>
            </div>

            <Nav variant="tabs" className="mb-3">
                <Nav.Item>
                    <Nav.Link 
                        active={activeTab === 'tickets'} 
                        onClick={() => {
                            setActiveTab('tickets');
                            setSelectedBrand(null);
                            loadTickets();
                        }}
                    >
                        All Tickets
                    </Nav.Link>
                </Nav.Item>
                <Nav.Item>
                    <Nav.Link 
                        active={activeTab === 'brands'} 
                        onClick={() => {
                            setActiveTab('brands');
                            setSelectedBrand(null);
                        }}
                    >
                        Brands & Channels
                    </Nav.Link>
                </Nav.Item>
                {selectedBrand && (
                    <Nav.Item>
                        <Nav.Link active={activeTab === 'brand-tickets'}>
                            {selectedBrand.name} Tickets
                        </Nav.Link>
                    </Nav.Item>
                )}
            </Nav>

            {activeTab === 'tickets' && (
                <div>
                    <h3>All Tickets</h3>
                    {loading ? (
                        <div className="text-center">
                            <Spinner animation="border" />
                        </div>
                    ) : error ? (
                        <Alert variant="danger">{error}</Alert>
                    ) : (
                        <>
                            {renderTicketsTable(tickets)}
                            {pagination.totalPages > 1 && (
                                renderPagination(pagination.totalPages, pagination.currentPage, handlePageChange)
                            )}
                        </>
                    )}
                </div>
            )}

            {activeTab === 'brands' && renderBrandsView()}
            {activeTab === 'brand-tickets' && renderBrandTicketsView()}
        </Container>
    );
};

export default Trengo; 